import * as signalR from "@microsoft/signalr";
import {
  remoteServiceBaseApiUrl,
  AUTH_TOKEN,
  TOKEN,
} from "src/shared/constants/AppConst";
import urlJoin from "url-join";
import { ChatMessageDto } from "./dto";

type MessageHandler = (message: ChatMessageDto) => void;
type MessageDeletedHandler = (messageId: string) => void;
type RoomUpdatedHandler = (roomId: string) => void;
type ReconnectedHandler = () => void;

class SignalRHub {
  private connection: signalR.HubConnection | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private messageHandlers: MessageHandler[] = [];
  private messageDeletedHandlers: MessageDeletedHandler[] = [];
  private roomUpdatedHandlers: RoomUpdatedHandler[] = [];
  private reconnectedHandlers: ReconnectedHandler[] = [];

  getCookieValue(key: string): string | null {
    const equalities = document.cookie.split("; ");
    for (let i = 0; i < equalities.length; i++) {
      if (!equalities[i]) {
        continue;
      }

      const splitted = equalities[i].split("=");
      if (splitted.length !== 2) {
        continue;
      }

      if (decodeURIComponent(splitted[0]) === key) {
        return decodeURIComponent(splitted[1] || "");
      }
    }

    return null;
  }

  async startConnection(): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      return;
    }

    const token = this.getCookieValue(TOKEN) ?? this.getCookieValue(AUTH_TOKEN);

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(urlJoin(remoteServiceBaseApiUrl, "chathub"), {
        accessTokenFactory: () => token || "",
        skipNegotiation: false,
        transport:
          signalR.HttpTransportType.WebSockets |
          signalR.HttpTransportType.ServerSentEvents |
          signalR.HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          if (retryContext.previousRetryCount < this.maxReconnectAttempts) {
            return Math.min(
              1000 * Math.pow(2, retryContext.previousRetryCount),
              30000,
            );
          }
          return null;
        },
      })
      .configureLogging(signalR.LogLevel.Information)
      .build();

    this.connection.onreconnecting((error) => {
      console.warn("SignalR reconnecting:", error);
      this.reconnectAttempts++;
    });

    this.connection.onreconnected((connectionId) => {
      this.reconnectAttempts = 0;
      this.reconnectedHandlers.forEach((handler) => handler());
    });

    this.connection.onclose((error) => {
      console.error("SignalR connection closed:", error);
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        setTimeout(() => this.startConnection(), 5000);
      }
    });

    // Set up event handlers
    this.setupEventHandlers();

    try {
      await this.connection.start();
      // Covers the manual restart in onclose too, which never fires onreconnected
      this.reconnectedHandlers.forEach((handler) => handler());
    } catch (error) {
      console.error("Error starting SignalR connection:", error);
      throw error;
    }
  }

  private setupEventHandlers(): void {
    if (!this.connection) return;

    // Receive new message
    this.connection.on("ReceiveMessage", (message: ChatMessageDto) => {
      this.messageHandlers.forEach((handler) => handler(message));
    });

    // Message deleted
    this.connection.on("MessageDeleted", (messageId: string) => {
      this.messageDeletedHandlers.forEach((handler) => handler(messageId));
    });

    // Room updated
    this.connection.on("RoomUpdated", (roomId: string) => {
      this.roomUpdatedHandlers.forEach((handler) => handler(roomId));
    });
  }

  async stopConnection(): Promise<void> {
    if (this.connection) {
      await this.connection.stop();
      this.connection = null;
    }
  }

  async joinRoom(roomId: string): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      await this.connection.invoke("JoinChatRoom", roomId);
    }
  }

  async leaveRoom(roomId: string): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      await this.connection.invoke("LeaveChatRoom", roomId);
    }
  }

  onReceiveMessage(callback: MessageHandler): () => void {
    this.messageHandlers.push(callback);
    return () => {
      this.messageHandlers = this.messageHandlers.filter((h) => h !== callback);
    };
  }

  onMessageDeleted(callback: MessageDeletedHandler): () => void {
    this.messageDeletedHandlers.push(callback);
    return () => {
      this.messageDeletedHandlers = this.messageDeletedHandlers.filter(
        (h) => h !== callback,
      );
    };
  }

  onReconnected(callback: ReconnectedHandler): () => void {
    this.reconnectedHandlers.push(callback);
    return () => {
      this.reconnectedHandlers = this.reconnectedHandlers.filter(
        (h) => h !== callback,
      );
    };
  }

  onRoomUpdated(callback: RoomUpdatedHandler): () => void {
    this.roomUpdatedHandlers.push(callback);
    return () => {
      this.roomUpdatedHandlers = this.roomUpdatedHandlers.filter(
        (h) => h !== callback,
      );
    };
  }

  getConnectionState(): signalR.HubConnectionState | null {
    return this.connection?.state || null;
  }

  isConnected(): boolean {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }
}

export const signalRHub = new SignalRHub();
